/**
 * Gerador de planilha (.xlsx) com cara de relatório corporativo
 * -----------------------------------------------------------------------------
 * Produz um arquivo com cabeçalho de marca (logo + nome da empresa + título da
 * tela exportada + metadados) seguido da tabela de dados formatada:
 *   • Faixa de títulos escura com texto branco, congelada e com autofiltro;
 *   • Linhas zebradas, bordas suaves e larguras calculadas;
 *   • Datas, valores e números com formato numérico real do Excel.
 *
 * Estrutura genérica: serve a qualquer módulo do sistema (basta informar
 * título, colunas e linhas). O mesmo desenho é replicado no servidor
 * (supabase/functions/export-data-email/xlsxReport.ts) para que o arquivo
 * baixado e o arquivo enviado por e-mail sejam idênticos.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial (substitui a planilha "crua" do SheetJS).
 */

import type { ExportColumn } from './types';
import { createLogger } from '@/lib/logger';

const logger = createLogger('xlsxReport');

/** Paleta fixa do relatório (o Excel exige cores literais em ARGB) */
const COR = {
  faixa: 'FF111827', // cabeçalho da tabela (cinza-900)
  faixaTexto: 'FFFFFFFF',
  zebra: 'FFF6F7F9',
  borda: 'FFE5E7EB',
  titulo: 'FF111827',
  apoio: 'FF6B7280',
  destaque: 'FF2563EB',
} as const;

export interface BrandedXlsxOptions {
  /** Título da tela exportada (ex.: "CRM — Negócios") */
  titulo: string;
  /** Nome da empresa (tenant) exibido no cabeçalho */
  empresa: string;
  /** URL da logo (opcional; falhas de download são ignoradas) */
  logoUrl?: string | null;
  /** Quem solicitou a exportação */
  usuario?: string | null;
  /** Momento da geração */
  geradoEm?: Date;
  /** Nome da aba */
  nomeAba?: string;
  /** Colunas escolhidas, na ordem do arquivo */
  colunas: ExportColumn[];
  /** Linhas cruas (chaves = ExportColumn.key) */
  linhas: Record<string, unknown>[];
}

/** Formato numérico do Excel conforme o tipo da coluna */
function formatoNumerico(coluna: ExportColumn, moeda: string): string | undefined {
  switch (coluna.kind) {
    case 'currency':
      return moeda === 'BRL' ? 'R$ #,##0.00' : '#,##0.00';
    case 'number':
      return '#,##0.##';
    case 'date':
      return 'dd/mm/yyyy';
    case 'datetime':
      return 'dd/mm/yyyy hh:mm';
    default:
      return undefined;
  }
}

/** Converte o valor cru no tipo nativo mais adequado para a célula */
function valorDaCelula(coluna: ExportColumn, valor: unknown): string | number | Date | null {
  if (valor === null || valor === undefined || valor === '') return null;

  switch (coluna.kind) {
    case 'number':
    case 'currency': {
      const numero = Number(valor);
      return Number.isNaN(numero) ? String(valor) : numero;
    }
    case 'date':
    case 'datetime': {
      const data = new Date(String(valor));
      return Number.isNaN(data.getTime()) ? String(valor) : data;
    }
    case 'boolean':
      return valor ? 'Sim' : 'Não';
    default:
      return String(valor);
  }
}

/** Baixa a logo e devolve o buffer + extensão (null quando indisponível) */
async function baixarLogo(
  url: string,
): Promise<{ buffer: ArrayBuffer; extensao: 'png' | 'jpeg' } | null> {
  try {
    const resposta = await fetch(url);
    if (!resposta.ok) return null;
    const tipo = resposta.headers.get('content-type') || '';
    if (tipo.includes('svg')) return null; // Excel não aceita SVG
    const buffer = await resposta.arrayBuffer();
    return { buffer, extensao: tipo.includes('jpeg') || tipo.includes('jpg') ? 'jpeg' : 'png' };
  } catch (erro) {
    logger.warn('Não foi possível carregar a logo para a planilha:', erro);
    return null;
  }
}

/** Monta o arquivo .xlsx completo e devolve o buffer pronto para download */
export async function buildBrandedXlsx(opcoes: BrandedXlsxOptions): Promise<ArrayBuffer> {
  // Import dinâmico: mantém a biblioteca fora do pacote inicial da aplicação
  const ExcelJS = (await import('exceljs')).default;

  const geradoEm = opcoes.geradoEm ?? new Date();
  const pasta = new ExcelJS.Workbook();
  pasta.creator = opcoes.empresa;
  pasta.created = geradoEm;

  const aba = pasta.addWorksheet((opcoes.nomeAba || 'Dados').slice(0, 31), {
    views: [{ state: 'frozen', ySplit: 6 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });

  const totalColunas = Math.max(opcoes.colunas.length, 4);
  const ultimaLetra = aba.getColumn(totalColunas).letter;

  // ===== Cabeçalho de marca (linhas 1 a 4) =====
  aba.getRow(1).height = 26;
  aba.getRow(2).height = 20;
  aba.getRow(3).height = 16;
  aba.getRow(4).height = 10;

  aba.mergeCells(`C1:${ultimaLetra}1`);
  const celulaEmpresa = aba.getCell('C1');
  celulaEmpresa.value = opcoes.empresa;
  celulaEmpresa.font = { name: 'Arial', size: 16, bold: true, color: { argb: COR.titulo } };
  celulaEmpresa.alignment = { vertical: 'middle' };

  aba.mergeCells(`C2:${ultimaLetra}2`);
  const celulaTitulo = aba.getCell('C2');
  celulaTitulo.value = opcoes.titulo;
  celulaTitulo.font = { name: 'Arial', size: 12, bold: true, color: { argb: COR.destaque } };
  celulaTitulo.alignment = { vertical: 'middle' };

  aba.mergeCells(`C3:${ultimaLetra}3`);
  const celulaMeta = aba.getCell('C3');
  celulaMeta.value = [
    `Gerado em ${geradoEm.toLocaleString('pt-BR')}`,
    opcoes.usuario ? `por ${opcoes.usuario}` : null,
    `${opcoes.linhas.length.toLocaleString('pt-BR')} registro(s)`,
  ]
    .filter(Boolean)
    .join('  •  ');
  celulaMeta.font = { name: 'Arial', size: 9, color: { argb: COR.apoio } };
  celulaMeta.alignment = { vertical: 'middle' };

  // Logo ancorada nas duas primeiras colunas (quando disponível)
  if (opcoes.logoUrl) {
    const logo = await baixarLogo(opcoes.logoUrl);
    if (logo) {
      const idImagem = pasta.addImage({ buffer: logo.buffer as never, extension: logo.extensao });
      aba.addImage(idImagem, {
        tl: { col: 0.2, row: 0.2 },
        ext: { width: 130, height: 44 },
        editAs: 'oneCell',
      });
    }
  }

  // ===== Faixa de títulos da tabela (linha 6) =====
  const linhaTitulos = aba.getRow(6);
  linhaTitulos.height = 22;
  opcoes.colunas.forEach((coluna, indice) => {
    const celula = linhaTitulos.getCell(indice + 1);
    celula.value = coluna.label;
    celula.font = { name: 'Arial', size: 10, bold: true, color: { argb: COR.faixaTexto } };
    celula.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COR.faixa } };
    celula.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  });

  // ===== Corpo da tabela =====
  opcoes.linhas.forEach((registro, indice) => {
    const moeda = typeof registro.moeda === 'string' ? (registro.moeda as string) : 'BRL';
    const linha = aba.getRow(7 + indice);
    opcoes.colunas.forEach((coluna, coluna_indice) => {
      const celula = linha.getCell(coluna_indice + 1);
      celula.value = valorDaCelula(coluna, registro[coluna.key]);
      const formato = formatoNumerico(coluna, moeda);
      if (formato) celula.numFmt = formato;
      celula.font = { name: 'Arial', size: 10 };
      celula.alignment = { vertical: 'middle', horizontal: 'left', indent: 1, wrapText: false };
      if (indice % 2 === 1) {
        celula.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COR.zebra } };
      }
      celula.border = {
        bottom: { style: 'thin', color: { argb: COR.borda } },
      };
    });
  });

  // ===== Larguras e autofiltro =====
  opcoes.colunas.forEach((coluna, indice) => {
    let maior = coluna.label.length;
    for (const registro of opcoes.linhas) {
      const valor = registro[coluna.key];
      if (valor === null || valor === undefined) continue;
      const tamanho = String(valor).length;
      if (tamanho > maior) maior = tamanho;
    }
    aba.getColumn(indice + 1).width = Math.min(Math.max(maior + 4, 12), 48);
  });

  if (opcoes.linhas.length > 0) {
    aba.autoFilter = {
      from: { row: 6, column: 1 },
      to: { row: 6 + opcoes.linhas.length, column: opcoes.colunas.length },
    };
  }

  return (await pasta.xlsx.writeBuffer()) as ArrayBuffer;
}
